import { MouseEvent, useEffect, useRef } from 'react';
import {
  Paper,
  SelectChangeEvent,
  Table as MuiTable,
  TableBody,
  TableFooter,
  TableRow,
} from '@mui/material';
import { capitalize, Loader, pluralize, TableContainer } from '@shared';
import { TableHead } from './TableHead';
import { EnhancedTablePropsType } from './EnhancedTable.types';
import * as S from '../SimpleTable/SimpleTable.styled';
import * as S2 from './EnhancedTable.styled';
import { useSearchParams } from 'react-router-dom';
import EnhancedPagination from '../../../pages/ExamSessions/components/EnhancedPagination';
import { TableFilters } from './TableFilters';

export function EnhancedTable<Entity extends { uuid: string; clickable?: boolean }, ColumnIds>(
  props: EnhancedTablePropsType<Entity, ColumnIds>
) {
  const {
    rows,
    columns,
    onRowClick,
    renderElems,
    entityName,
    totalPages,
    totalRows,
    onPageChange,
    currentPage,
    dispatch,
    sortingState,
    filterProps,
    tableLoadingControls,
  } = props;

  const { orderBy, order, rowsPerPage, auditOfInterest } = sortingState;
  const [, setSearchParams] = useSearchParams();
  const { isTableDataLoading: isLoading, setIsTableDataLoading: setIsLoading } =
    tableLoadingControls;
  const prevValues = useRef({ currentPage, totalPages, rows });

  useEffect(() => {
    const hasRowsArrayChanged = prevValues.current.rows !== rows;
    prevValues.current.rows = rows;

    let loadingTimeout: ReturnType<typeof setTimeout> | null = null;

    if (isLoading && hasRowsArrayChanged) {
      loadingTimeout = setTimeout(() => {
        setIsLoading(false);
      }, 300);
    }

    return () => {
      if (loadingTimeout !== null) clearTimeout(loadingTimeout);
    };
  }, [rows, isLoading, setIsLoading]);

  const handleRequestSort = (event: MouseEvent<unknown>, property: ColumnIds) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    dispatch && dispatch({ type: 'sort', payload: { order: newOrder, orderBy: property } });
    setSearchParams((prevParams) => ({
      page: prevParams.get('page') || '1',
      limit: prevParams.get('limit') || '20',
      field: String(property),
      direction: newOrder,
    }));
    setIsLoading(true);
  };
  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    const newLimit = event.target.value;
    dispatch && dispatch({ type: 'rowsPerPage', payload: Number(newLimit) });
    onPageChange && onPageChange(1);
    setSearchParams((prevParams) => ({
      limit: String(newLimit),
      field: prevParams.get('field') || 'first_name',
      direction: prevParams.get('direction') || 'asc',
    }));
    if (totalRows && newLimit >= totalRows && totalRows == rows.length) {
      return;
    }
    setIsLoading(true);
  };
  const handleOnPageChange = (page: number) => {
    if (!onPageChange) {
      console.warn('Pagination warning: unable to navigate to the requested page.');
      return;
    }

    onPageChange(page);
    setIsLoading(true);
  };
  const filterByAuditOfInterest = (value: boolean) => {
    dispatch && dispatch({ type: 'auditOfInterest', payload: value });
    onPageChange && onPageChange(1);
    setSearchParams((prevParams) => ({
      ...prevParams,
      auditOfInterest: value || false,
    }));
    setIsLoading(true);
  };

  const renderRow = (row: Entity) =>
    row.clickable ? (
      <TableRow
        sx={{ cursor: 'pointer' }}
        hover
        key={row.uuid}
        onClick={() => onRowClick({ [entityName]: row.uuid })}
        data-testid={`${entityName}-table-row`}
      >
        {renderElems(row)}
      </TableRow>
    ) : (
      <TableRow hover key={row.uuid} data-testid={`${entityName}-table-row`}>
        {renderElems(row)}
      </TableRow>
    );

  // Avoid a layout jump when reaching the last page with empty rows.
  // const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0
  return (
    <TableContainer noBorders component={Paper} sx={{ userSelect: 'none', position: 'relative' }}>
      <TableFilters
        isAuditOfInterest={auditOfInterest}
        filterByAuditOfInterest={filterByAuditOfInterest}
        filterProps={filterProps}
      ></TableFilters>
      <MuiTable>
        <TableHead
          columns={columns}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
          entityName={entityName}
        />
        <TableBody>
          {rows.length ? (
            rows
              // .sort(getComparator(order, orderBy))
              // .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => renderRow(row))
          ) : (
            <S.TableRow data-testid={`${entityName}-empty-table-row`}>
              <S.TableCell colSpan={7} sx={{ textAlign: 'center' }}>
                <i>No {pluralize(capitalize(entityName))} Found</i>
              </S.TableCell>
            </S.TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow sx={{ '& td': { borderBottom: 'none !important' } }}>
            <S2.FooterCell colSpan={7}>
              <EnhancedPagination
                totalRows={totalRows || rows.length}
                currentPage={currentPage || 1}
                totalPages={totalPages || 1}
                rowsPerPage={rowsPerPage}
                onPageChange={handleOnPageChange}
                onRowsPerPageChange={(event) => handleChangeRowsPerPage(event)}
              />
            </S2.FooterCell>
          </TableRow>
        </TableFooter>
      </MuiTable>
      {isLoading && <Loader />}
    </TableContainer>
  );
}
